/**
 * Lab — Geometric Validation Workflow — V14.62 Sprint 19.
 *
 * Source unique de vérité du « micro-IFC ». Évolutions Sprint 19 :
 *   • `scale_m_per_px` à la racine du JSON (échelle métrique réelle)
 *   • Outil CALIBRATE pour mesurer A→B et saisir la distance réelle
 *   • Middleware `zundo` (temporal) → undo / redo natif (Ctrl+Z, Ctrl+Y)
 *
 * Schéma JSON courant (sortie `toMicroIFC()`) :
 * {
 *   "scale_m_per_px": 0.0123,
 *   "walls":   [{ id, x1, y1, x2, y2, thickness }],
 *   "windows": [{ id, wallId, t, width }]
 * }
 *
 * Notes :
 *  - `temporal` ne suit QUE les champs énumérés dans `partialize` ; les états
 *    transitoires (outil actif, drawingFrom, modale calibration ouverte) sont
 *    exclus → Ctrl+Z ne change pas l'outil, il ne touche qu'à la donnée.
 *  - `addWall` snappe le 2ᵉ point sur multiple de 90° si `orthoSnap=true`.
 */
import { create } from 'zustand';
import { temporal } from 'zundo';

const uid = (prefix) => `${prefix}${Math.random().toString(36).slice(2, 7)}`;

const INITIAL_WALLS = [
  { id: 'w1', x1: 120, y1: 120, x2: 520, y2: 120, thickness: 8, kind: 'cloison_fine' },
  { id: 'w2', x1: 520, y1: 120, x2: 520, y2: 380, thickness: 8, kind: 'cloison_fine' },
  { id: 'w3', x1: 520, y1: 380, x2: 220, y2: 380, thickness: 8, kind: 'cloison_fine' },
];

/**
 * Wall types — aligned with the production Édition Plan flow (PlanEditorContext).
 * `defaultThickness` is expressed in canvas pixels for the seed; once a project
 * is calibrated, the inspector converts to/from cm using `scale_m_per_px`.
 */
export const WALL_KINDS = Object.freeze({
  PORTEUR:       'porteur',
  CLOISON_FINE:  'cloison_fine',
  CLOISON_BASSE: 'cloison_basse',
});

export const WALL_KIND_DEFS = [
  { value: 'porteur',       label: 'Mur porteur',
    color: '#06b6d4', defaultThickness: 18, strokeWidth: 4,
    description: 'Mur structurel — porteur de charges (18 cm)' },
  { value: 'cloison_fine',  label: 'Cloison fine',
    color: '#ef4444', defaultThickness: 8,  strokeWidth: 2,
    description: 'Cloison standard de séparation (8 cm)' },
  { value: 'cloison_basse', label: 'Cloison basse',
    color: '#a855f7', defaultThickness: 5,  strokeWidth: 2,
    description: 'Séparation visuelle partielle (5 cm)' },
];

export const wallKindFor = (kind) =>
  WALL_KIND_DEFS.find((k) => k.value === kind) || WALL_KIND_DEFS[1];

export const TOOLS = Object.freeze({
  SELECT:        'select',
  DRAW_WALL:     'draw_wall',
  PLACE_WINDOW:  'place_window',
  PLACE_DOOR:    'place_door',
  PLACE_STAIRS:  'place_stairs',
  DRAW_TERRAIN:  'draw_terrain',
  DRAW_SKEW:     'draw_skew',   // Sprint 31 — ligne de brisure (mansarde)
  PLACE_VELUX:   'place_velux', // Sprint 31 Phase 2 — velux sur mansarde
  DELETE:        'delete',
  CALIBRATE:     'calibrate',
});

export const STAIRS_KINDS = Object.freeze({
  STRAIGHT: 'straight',  // Escalier droit (1 volée)
  TURNING:  'turning',   // Tournant (quart ou demi-tour)
  SPIRAL:   'spiral',    // Colimaçon
});

/**
 * Snap a point B to a 0°/90°/180°/270° angle around point A.
 * Returns { x, y } aligned on the dominant axis (the longer projection).
 */
export const orthoSnapPoint = (ax, ay, bx, by) => {
  const dx = bx - ax;
  const dy = by - ay;
  return Math.abs(dx) >= Math.abs(dy)
    ? { x: bx, y: ay }
    : { x: ax, y: by };
};

const baseCreator = (set, get) => ({
  // ─────── Persisted (tracked by temporal middleware) ───────
  walls: INITIAL_WALLS,
  windows: [],
  doors: [],
  // Stairs / trémies : rectangle zones with a stair type. Independent of walls
  // because a stair often crosses a room interior, not a wall edge.
  stairs: [],
  // Terrain / property boundary : closed polygon drawn by the user (typically
  // on the RDC). One per floor — but only the RDC's polygon is used by the
  // exterior generator. Sprint 24bis.
  terrain: [],
  // Sprint 31 — Lignes de brisure (mansardes). Chaque ligne indique où le
  // plafond commence à s'abaisser : la HSP est pleine côté haut, réduite à
  // `low_side_hsp_m` côté bas. Coordonnées en pixels canvas comme les murs.
  //   { id, x1, y1, x2, y2, low_side_hsp_m?: 1.20 }
  skew_edges: [],
  // Sprint 31 Phase 2 — Velux (fenêtres de toit) placées sur les lignes
  // de brisure. `skewId` référence le skew_edge parent, `t` est la
  // position paramétrique [0..1] le long de la ligne (0 = point A, 1 = B).
  //   { id, skewId, t, width_m: 0.78, height_m: 1.18 }
  velux: [],
  scale_m_per_px: 0,
  calibration: null,
  // Sprint 26F — Per-floor alignment transform { tx, ty, scale, rot } used by
  // the FloorAlignmentEditor to overlay floors onto each other. Persisted in
  // the floor geometry doc but transparent to the regular editor.
  transform: null,
  // ─────── Transient state (excluded from undo history) ─────
  selectedId: null,
  tool: TOOLS.SELECT,
  drawingFrom: null,
  /** First vertex of the current wall polyline chain — used to close
   *  the chain on double-click (forms a closed room when the user has
   *  traced at least 3 segments). */
  chainStart: null,
  /** Default kind used by the wall-drawing tool. Changed via the toolbar
   *  pill picker so the user can tag the polyline before drawing it. */
  drawWallKind: 'cloison_fine',
  calibrationFrom: null,
  calibrationPending: null,
  stairsFrom: null,         // first click of the stairs rectangle
  stairsPending: null,      // { x, y, w, h } awaiting type modal
  // Terrain authoring : current in-progress polygon (array of {x,y}). When
  // the user double-clicks or clicks the first vertex, the polygon is
  // committed to `terrain` and this is reset.
  terrainDraft: [],
  backgroundImage: null,
  viewport: { scale: 1, x: 0, y: 0 },

  // ──────────────────────────────────────────────────────────
  // Tool selection
  // ──────────────────────────────────────────────────────────
  setTool: (tool) => set({
    tool,
    drawingFrom: null,
    chainStart: null,
    calibrationFrom: null,
    calibrationPending: null,
    stairsFrom: null,
    stairsPending: null,
    terrainDraft: [],
    selectedId: null,
  }),

  setDrawWallKind: (kind) => set({
    drawWallKind: WALL_KIND_DEFS.find((k) => k.value === kind) ? kind : 'cloison_fine',
  }),

  // ──────────────────────────────────────────────────────────
  // Selection
  // ──────────────────────────────────────────────────────────
  selectWall: (id) => set({ selectedId: id }),
  clearSelection: () => set({ selectedId: null }),

  // ──────────────────────────────────────────────────────────
  // Walls CRUD
  // ──────────────────────────────────────────────────────────
  addWall: (x1, y1, x2, y2, opts = {}) => {
    const kind = opts.kind || 'cloison_fine';
    const def = WALL_KIND_DEFS.find((k) => k.value === kind) || WALL_KIND_DEFS[1];
    const thickness = Number.isFinite(opts.thickness) ? opts.thickness : def.defaultThickness;
    const id = uid('w_');
    set((s) => ({
      walls: [...s.walls, {
        id,
        x1: Math.round(x1), y1: Math.round(y1),
        x2: Math.round(x2), y2: Math.round(y2),
        thickness, kind,
      }],
    }));
    return id;
  },

  /** Patch arbitrary wall fields — used by the inspector for type/thickness edits. */
  updateWall: (id, patch) => set((s) => ({
    walls: s.walls.map((w) => {
      if (w.id !== id) return w;
      const next = { ...w, ...patch };
      // Re-align thickness with the new kind's default if the caller only
      // changed `kind` (no explicit thickness). This is the same behavior
      // the production flow has via the right-panel chip selector.
      if (patch.kind && patch.thickness === undefined) {
        const def = WALL_KIND_DEFS.find((k) => k.value === patch.kind);
        if (def) next.thickness = def.defaultThickness;
      }
      // Clamp thickness to a sensible range (in canvas px).
      if (next.thickness !== undefined) {
        next.thickness = Math.max(2, Math.min(80, Math.round(next.thickness)));
      }
      return next;
    }),
  })),

  updateWallEndpoint: (id, endpoint, x, y) => set((s) => ({
    walls: s.walls.map((w) => {
      if (w.id !== id) return w;
      return endpoint === 'a'
        ? { ...w, x1: Math.round(x), y1: Math.round(y) }
        : { ...w, x2: Math.round(x), y2: Math.round(y) };
    }),
  })),

  translateWall: (id, dx, dy) => set((s) => ({
    walls: s.walls.map((w) =>
      w.id === id
        ? { ...w, x1: w.x1 + Math.round(dx), y1: w.y1 + Math.round(dy),
                  x2: w.x2 + Math.round(dx), y2: w.y2 + Math.round(dy) }
        : w),
  })),

  deleteWall: (id) => set((s) => ({
    walls: s.walls.filter((w) => w.id !== id),
    windows: s.windows.filter((win) => win.wallId !== id),
    doors: s.doors.filter((d) => d.wallId !== id),
    selectedId: s.selectedId === id ? null : s.selectedId,
  })),

  // ──────────────────────────────────────────────────────────
  // Windows
  // ──────────────────────────────────────────────────────────
  placeWindow: (wallId, t, width = 0.18) => set((s) => ({
    windows: [...s.windows, {
      id: uid('win_'), wallId,
      t: Math.max(0.05, Math.min(0.95, t)),
      width: Math.max(0.05, Math.min(0.5, width)),
    }],
  })),

  deleteWindow: (id) => set((s) => ({
    windows: s.windows.filter((w) => w.id !== id),
  })),

  updateWindow: (id, patch) => set((s) => ({
    windows: s.windows.map((w) => {
      if (w.id !== id) return w;
      const next = { ...w, ...patch };
      if (next.t !== undefined)     next.t     = Math.max(0.02, Math.min(0.98, +next.t));
      if (next.width !== undefined) next.width = Math.max(0.03, Math.min(0.6,  +next.width));
      return next;
    }),
  })),

  // ──────────────────────────────────────────────────────────
  // Doors — same parametric data shape as windows but logically separate.
  // Visually rendered with an arc to show the door swing direction.
  // ──────────────────────────────────────────────────────────
  placeDoor: (wallId, t, width = 0.12) => set((s) => ({
    doors: [...s.doors, {
      id: uid('door_'), wallId,
      t: Math.max(0.05, Math.min(0.95, t)),
      width: Math.max(0.05, Math.min(0.5, width)),
    }],
  })),

  deleteDoor: (id) => set((s) => ({
    doors: s.doors.filter((d) => d.id !== id),
  })),

  updateDoor: (id, patch) => set((s) => ({
    doors: s.doors.map((d) => {
      if (d.id !== id) return d;
      const next = { ...d, ...patch };
      if (next.t !== undefined)     next.t     = Math.max(0.02, Math.min(0.98, +next.t));
      if (next.width !== undefined) next.width = Math.max(0.03, Math.min(0.6,  +next.width));
      return next;
    }),
  })),

  // ──────────────────────────────────────────────────────────
  // Stairs / trémies — drawn as a rectangle zone with a type.
  // ──────────────────────────────────────────────────────────
  setStairsFrom: (pt) => set({ stairsFrom: pt }),

  beginStairs: (x, y, w, h) => set({
    stairsPending: { x, y, w, h },
    stairsFrom: null,
  }),

  cancelStairs: () => set({ stairsPending: null }),

  commitStairs: (kind) => set((s) => {
    const p = s.stairsPending;
    if (!p) return {};
    const validKinds = Object.values(STAIRS_KINDS);
    return {
      stairs: [...s.stairs, {
        id: uid('stair_'),
        x: Math.round(p.x), y: Math.round(p.y),
        w: Math.round(p.w), h: Math.round(p.h),
        kind: validKinds.includes(kind) ? kind : STAIRS_KINDS.STRAIGHT,
      }],
      stairsPending: null,
    };
  }),

  deleteStairs: (id) => set((s) => ({
    stairs: s.stairs.filter((st) => st.id !== id),
  })),

  /** Patch an existing staircase — dimensions (w, h), position (x, y), or kind. */
  updateStairs: (id, patch) => set((s) => ({
    stairs: s.stairs.map((st) => {
      if (st.id !== id) return st;
      const next = { ...st, ...patch };
      ['x', 'y', 'w', 'h'].forEach((k) => {
        if (next[k] !== undefined) next[k] = Math.round(+next[k]);
      });
      if (next.w !== undefined) next.w = Math.max(8, next.w);
      if (next.h !== undefined) next.h = Math.max(8, next.h);
      if (next.kind && !Object.values(STAIRS_KINDS).includes(next.kind)) {
        next.kind = STAIRS_KINDS.STRAIGHT;
      }
      return next;
    }),
  })),

  // ──────────────────────────────────────────────────────────
  // Terrain — closed polygon (property / garden boundary).
  //
  // Authoring flow :
  //   1. user activates the DRAW_TERRAIN tool
  //   2. each canvas click appends one vertex to `terrainDraft`
  //   3. double-click OR clicking near the first vertex closes the polygon
  //      → commitTerrain() copies it to `terrain` and clears the draft.
  //
  // Only the RDC's polygon is later used by the exterior generator.
  // ──────────────────────────────────────────────────────────
  pushTerrainVertex: (x, y) => set((s) => ({
    terrainDraft: [...s.terrainDraft, { x: Math.round(x), y: Math.round(y) }],
  })),

  cancelTerrainDraft: () => set({ terrainDraft: [] }),

  commitTerrain: () => set((s) => {
    if (s.terrainDraft.length < 3) return { terrainDraft: [] };
    return { terrain: s.terrainDraft.slice(), terrainDraft: [] };
  }),

  clearTerrain: () => set({ terrain: [], terrainDraft: [] }),

  // ──────────────────────────────────────────────────────────
  // Sprint 31 — Skew edges (lignes de brisure pour mansardes)
  // ──────────────────────────────────────────────────────────
  addSkewEdge: (x1, y1, x2, y2, opts = {}) => {
    const id = uid('sk_');
    set((s) => ({
      skew_edges: [...s.skew_edges, {
        id,
        x1: Math.round(x1), y1: Math.round(y1),
        x2: Math.round(x2), y2: Math.round(y2),
        low_side_hsp_m: Number.isFinite(opts.low_side_hsp_m)
          ? +opts.low_side_hsp_m : 1.20,
      }],
    }));
    return id;
  },

  updateSkewEdge: (id, patch) => set((s) => ({
    skew_edges: s.skew_edges.map((e) => {
      if (e.id !== id) return e;
      const next = { ...e, ...patch };
      if (patch.low_side_hsp_m !== undefined) {
        next.low_side_hsp_m = Math.max(0.5, Math.min(4.0, +patch.low_side_hsp_m));
      }
      return next;
    }),
  })),

  updateSkewEdgeEndpoint: (id, endpoint, x, y) => set((s) => ({
    skew_edges: s.skew_edges.map((e) => {
      if (e.id !== id) return e;
      return endpoint === 'a'
        ? { ...e, x1: Math.round(x), y1: Math.round(y) }
        : { ...e, x2: Math.round(x), y2: Math.round(y) };
    }),
  })),

  translateSkewEdge: (id, dx, dy) => set((s) => ({
    skew_edges: s.skew_edges.map((e) =>
      e.id === id
        ? { ...e, x1: e.x1 + Math.round(dx), y1: e.y1 + Math.round(dy),
                  x2: e.x2 + Math.round(dx), y2: e.y2 + Math.round(dy) }
        : e),
  })),

  deleteSkewEdge: (id) => set((s) => ({
    skew_edges: s.skew_edges.filter((e) => e.id !== id),
    velux: s.velux.filter((v) => v.skewId !== id),  // cascade
    selectedId: s.selectedId === id ? null : s.selectedId,
  })),

  // ──────────────────────────────────────────────────────────
  // Sprint 31 Phase 2 — Velux (fenêtres de toit sur mansardes)
  // ──────────────────────────────────────────────────────────
  addVelux: (skewId, t, opts = {}) => {
    const id = uid('vx_');
    set((s) => ({
      velux: [...s.velux, {
        id, skewId,
        t: Math.max(0, Math.min(1, +t)),
        width_m:  Number.isFinite(opts.width_m)  ? +opts.width_m  : 0.78,  // Velux UK04 standard ~78 cm
        height_m: Number.isFinite(opts.height_m) ? +opts.height_m : 1.18,  // ~118 cm
      }],
    }));
    return id;
  },

  updateVelux: (id, patch) => set((s) => ({
    velux: s.velux.map((v) => {
      if (v.id !== id) return v;
      const next = { ...v, ...patch };
      if (patch.t !== undefined) next.t = Math.max(0, Math.min(1, +patch.t));
      if (patch.width_m !== undefined)  next.width_m  = Math.max(0.4, Math.min(2.0, +patch.width_m));
      if (patch.height_m !== undefined) next.height_m = Math.max(0.6, Math.min(2.5, +patch.height_m));
      return next;
    }),
  })),

  deleteVelux: (id) => set((s) => ({
    velux: s.velux.filter((v) => v.id !== id),
    selectedId: s.selectedId === id ? null : s.selectedId,
  })),

  // ──────────────────────────────────────────────────────────
  // Background plan image (imported by the user)
  // ──────────────────────────────────────────────────────────
  setBackgroundImage: (bg) => set({ backgroundImage: bg }),
  clearBackgroundImage: () => set({ backgroundImage: null }),

  // ──────────────────────────────────────────────────────────
  // Viewport (zoom + pan)
  // ──────────────────────────────────────────────────────────
  setViewport: (vp) => set({ viewport: vp }),
  resetViewport: () => set({ viewport: { scale: 1, x: 0, y: 0 } }),

  // ──────────────────────────────────────────────────────────
  // Calibration — the ruler tool
  // ──────────────────────────────────────────────────────────
  setCalibrationFrom: (pt) => set({ calibrationFrom: pt }),

  /**
   * Called after the second click of the ruler tool. Opens the modal
   * by writing the segment to `calibrationPending` (the page reacts).
   */
  beginCalibration: (x1, y1, x2, y2) => set({
    calibrationPending: { x1, y1, x2, y2 },
    calibrationFrom: null,
  }),

  cancelCalibration: () => set({ calibrationPending: null }),

  /**
   * Commits the user-entered metric distance.
   * Computes scale = realMeters / pixelLength.
   */
  commitCalibration: (meters) => set((s) => {
    const seg = s.calibrationPending;
    if (!seg) return {};
    const px = Math.hypot(seg.x2 - seg.x1, seg.y2 - seg.y1);
    if (!Number.isFinite(meters) || meters <= 0 || px < 1) return { calibrationPending: null };
    return {
      scale_m_per_px: meters / px,
      calibration: { ...seg, meters },
      calibrationPending: null,
    };
  }),

  clearCalibration: () => set({
    scale_m_per_px: 0,
    calibration: null,
    calibrationPending: null,
  }),

  // ──────────────────────────────────────────────────────────
  // Live JSON export
  // ──────────────────────────────────────────────────────────
  toMicroIFC: () => {
    const s = get();
    const payload = {
      scale_m_per_px: +s.scale_m_per_px.toFixed(6),
      walls: s.walls.map(({ id, x1, y1, x2, y2, thickness, kind, height_override_m }) => ({
        id, x1, y1, x2, y2, thickness,
        kind: kind || 'cloison_fine',
        // Sprint 30 — hauteur spécifique (mansarde). null / undefined ⇒ HSP étage.
        ...(Number.isFinite(height_override_m) && height_override_m > 0
          ? { height_override_m: +(+height_override_m).toFixed(3) }
          : {}),
      })),
      windows: s.windows.map(({ id, wallId, t, width }) => ({
        id, wallId, t: +t.toFixed(3), width: +width.toFixed(3),
      })),
      doors: s.doors.map(({ id, wallId, t, width }) => ({
        id, wallId, t: +t.toFixed(3), width: +width.toFixed(3),
      })),
      stairs: s.stairs.map(({ id, x, y, w, h, kind }) => ({
        id, x, y, w, h, kind,
      })),
      terrain: s.terrain.map(({ x, y }) => ({
        x: Math.round(x), y: Math.round(y),
      })),
      // Sprint 31 — Lignes de brisure (mansardes).
      skew_edges: s.skew_edges.map(({ id, x1, y1, x2, y2, low_side_hsp_m }) => ({
        id, x1, y1, x2, y2,
        ...(Number.isFinite(low_side_hsp_m) && low_side_hsp_m > 0
          ? { low_side_hsp_m: +(+low_side_hsp_m).toFixed(2) }
          : {}),
      })),
      // Sprint 31 Phase 2 — Velux (fenêtres de toit).
      velux: s.velux.map(({ id, skewId, t, width_m, height_m }) => ({
        id, skewId,
        t: +(+t).toFixed(3),
        width_m:  +(+width_m).toFixed(2),
        height_m: +(+height_m).toFixed(2),
      })),
    };
    if (s.transform) payload.transform = s.transform;
    return payload;
  },

  // ──────────────────────────────────────────────────────────
  // Misc
  // ──────────────────────────────────────────────────────────
  resetSeed: () => set({
    walls: INITIAL_WALLS,
    windows: [],
    doors: [],
    stairs: [],
    terrain: [],
    skew_edges: [],  // Sprint 31
    velux: [],       // Sprint 31 Phase 2
    scale_m_per_px: 0,
    calibration: null,
    selectedId: null,
    tool: TOOLS.SELECT,
    drawingFrom: null,
    chainStart: null,
    drawWallKind: 'cloison_fine',
    calibrationFrom: null,
    calibrationPending: null,
    stairsFrom: null,
    stairsPending: null,
    terrainDraft: [],
    backgroundImage: null,
    viewport: { scale: 1, x: 0, y: 0 },
  }),

  setDrawingFrom: (pt) => set({ drawingFrom: pt }),
  setChainStart: (pt) => set({ chainStart: pt }),
  clearWallChain: () => set({ drawingFrom: null, chainStart: null }),

  // ──────────────────────────────────────────────────────────
  // Bulk load — V14.62 Sprint 22 (Single-House Mode).
  // Replaces the current geometry with a micro-IFC payload (typically the
  // geometry of another floor we're switching to). Resets transient UI
  // state so we don't carry an in-progress wall across floors.
  // Does NOT touch the background image / viewport (those are session UX).
  // ──────────────────────────────────────────────────────────
  loadFromMicroIFC: (payload) => set({
    walls: Array.isArray(payload?.walls)
      ? payload.walls.map((w) => ({ kind: 'cloison_fine', ...w }))
      : [],
    windows: Array.isArray(payload?.windows) ? payload.windows : [],
    doors: Array.isArray(payload?.doors) ? payload.doors : [],
    stairs: Array.isArray(payload?.stairs) ? payload.stairs : [],
    terrain: Array.isArray(payload?.terrain) ? payload.terrain : [],
    // Sprint 31
    skew_edges: Array.isArray(payload?.skew_edges) ? payload.skew_edges : [],
    // Sprint 31 Phase 2
    velux: Array.isArray(payload?.velux) ? payload.velux : [],
    scale_m_per_px: Number.isFinite(payload?.scale_m_per_px) ? payload.scale_m_per_px : 0,
    calibration: payload?.calibration || null,
    transform: payload?.transform || null,
    selectedId: null,
    tool: TOOLS.SELECT,
    drawingFrom: null,
    chainStart: null,
    calibrationFrom: null,
    calibrationPending: null,
    stairsFrom: null,
    stairsPending: null,
    terrainDraft: [],
  }),
});

/**
 * Wrap with `temporal` so Ctrl+Z / Ctrl+Y can rewind history. Only persisted
 * data fields are tracked — transient UI state is excluded so the cursor /
 * active tool / drawing scratchpad don't pollute history.
 */
export const useGeometryStore = create(
  temporal(baseCreator, {
    partialize: (state) => ({
      walls: state.walls,
      windows: state.windows,
      doors: state.doors,
      stairs: state.stairs,
      terrain: state.terrain,
      skew_edges: state.skew_edges,  // Sprint 31
      velux: state.velux,            // Sprint 31 Phase 2
      scale_m_per_px: state.scale_m_per_px,
      calibration: state.calibration,
    }),
    limit: 50,
    equality: (a, b) => JSON.stringify(a) === JSON.stringify(b),
  }),
);

/** Imperative accessor for the temporal store (undo / redo). */
export const temporalStore = () => useGeometryStore.temporal;
